import React, { useState, useRef, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { useGame } from '../../context/GameContext';
import { usePlayer } from '../../context/PlayerContext';
import { useGameRound } from '../../hooks/useGameRound';
import { canStartGame } from '../../utils/gameRules';
import { resizeImageToCardBack } from '../../utils/imageResize';
import Button from '../ui/Button';

const CARD_BACK_OPTIONS = [
  { id: 'classic', label: 'Classic', src: null },
  { id: 'badbunny', label: 'Bad Bunny', src: '/card-back-badbunny.jpg' },
];

function LobbyScreen() {
  const { state } = useGame();
  const { playerId, isHost } = usePlayer();
  const { markReady, startGame } = useGameRound();
  const { roomCode, players, cardBack, customCardBack } = state;

  const playerList = Object.entries(players);
  const me = players[playerId];
  const ready = me?.ready || false;
  const canStart = isHost && canStartGame(players);
  const selectedBack = cardBack || 'classic';

  // No auto-start — let the host pick a card back and start manually

  const [copied, setCopied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(roomCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const handleCardBackChange = useCallback(
    async (backId) => {
      if (!isHost) return;
      try {
        const fn = httpsCallable(functions, 'setCardBack');
        await fn({ roomCode, cardBack: backId });
      } catch {}
    },
    [isHost, roomCode]
  );

  const handleUpload = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file || !isHost) return;

      setUploading(true);
      try {
        const dataUrl = await resizeImageToCardBack(file);
        const fn = httpsCallable(functions, 'setCardBack');
        await fn({ roomCode, cardBack: 'custom', customCardBack: dataUrl });
      } catch (err) {
        console.error('Upload error:', err);
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [isHost, roomCode]
  );

  return (
    <div className="lobby-screen">
      <h2 className="lobby-screen__title">The Clubhouse</h2>

      <div className="lobby-screen__code" onClick={handleCopyCode} role="button" tabIndex={0}>
        <span className="lobby-screen__code-label">Room Code</span>
        <span className="lobby-screen__code-value">{roomCode}</span>
        <span className="lobby-screen__code-copied">{copied ? 'Copied!' : 'Tap to share'}</span>
      </div>

      <div className="lobby-screen__players">
        <h3>On the Tee ({playerList.length}/6)</h3>
        {playerList.map(([pid, player]) => (
          <div key={pid} className="lobby-screen__player">
            <span className="lobby-screen__player-name">
              {player.displayName}
              {player.isHost && ' (Host)'}
              {pid === playerId && ' (You)'}
            </span>
            <span
              className={`lobby-screen__player-status ${
                player.ready ? 'lobby-screen__player-status--ready' : ''
              }`}
            >
              {player.ready ? 'On the tee!' : 'Stretching...'}
            </span>
          </div>
        ))}
      </div>

      {isHost && (
        <div className="lobby-screen__card-backs">
          <h3>Card Back</h3>
          <div className="lobby-screen__card-back-grid">
            {CARD_BACK_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                className={`lobby-screen__card-back-option ${selectedBack === opt.id ? 'lobby-screen__card-back-option--active' : ''}`}
                onClick={() => handleCardBackChange(opt.id)}
              >
                {opt.src ? (
                  <img src={opt.src} alt={opt.label} />
                ) : (
                  <div className="lobby-screen__card-back-classic" />
                )}
                <span>{opt.label}</span>
              </button>
            ))}

            {/* Custom upload option */}
            <button
              className={`lobby-screen__card-back-option ${selectedBack === 'custom' ? 'lobby-screen__card-back-option--active' : ''}`}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {customCardBack ? (
                <img src={customCardBack} alt="Custom" />
              ) : (
                <div className="lobby-screen__card-back-upload">
                  {uploading ? '...' : '+'}
                </div>
              )}
              <span>{uploading ? 'Uploading' : 'Your Photo'}</span>
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleUpload}
            style={{ display: 'none' }}
          />
        </div>
      )}

      <div className="lobby-screen__actions">
        <Button
          variant={ready ? 'secondary' : 'primary'}
          onClick={markReady}
        >
          {ready ? 'Hold On...' : "I'm In"}
        </Button>

        {isHost && (
          <Button onClick={startGame} disabled={!canStart}>
            Let's Go!
          </Button>
        )}
      </div>

      {isHost && !canStart && (
        <p className="lobby-screen__waiting">
          Need at least 2 on the tee to start.
        </p>
      )}
    </div>
  );
}

export default LobbyScreen;
